/**
 * Drillz geometry scene API client (wolfwalker-api BFF).
 */
import { drillingService } from './drillingService';

const POLL_MS = 3000;
const POLL_MAX_MS = 30000;

class GeometryService {
  constructor() {
    this.functionAppUrl = drillingService.functionAppUrl;
  }

  async _headers() {
    return drillingService.getAuthenticatedHeaders();
  }

  /**
   * @param {object} opts
   * @param {string} opts.holeId
   * @param {number} [opts.radius=0]
   * @param {number|null} [opts.azimuth]
   * @param {number|null} [opts.sliceWidth]
   * @returns {Promise<object>}
   */
  async fetchGeometryScene({ holeId, radius = 0, azimuth = null, sliceWidth = null }) {
    const headers = await this._headers();
    const params = new URLSearchParams();
    params.set('hole_id', holeId);
    params.set('radius', String(radius));
    if (azimuth != null) params.set('azimuth', String(azimuth));
    if (sliceWidth != null) params.set('slice_width', String(sliceWidth));

    const url = `${this.functionAppUrl}/api/drillz_geometry_scene?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers });
    const text = await response.text();
    let result;
    try {
      result = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(`Geometry scene: invalid JSON (${response.status})`);
    }
    if (!response.ok || result.success === false) {
      throw new Error(result.message || result.error || `Geometry scene HTTP ${response.status}`);
    }
    return result;
  }

  /**
   * @param {string|string[]} collarUuidOrUuids
   * @param {string[]} tableDefinitionIds
   */
  async fetchGeometryVisuals(collarUuidOrUuids, tableDefinitionIds) {
    const ids = (tableDefinitionIds || []).filter(Boolean);
    const collarUuids = Array.isArray(collarUuidOrUuids)
      ? collarUuidOrUuids.filter(Boolean)
      : (collarUuidOrUuids ? [collarUuidOrUuids] : []);
    if (!collarUuids.length || !ids.length) {
      return { layers: [] };
    }
    const headers = await this._headers();
    const params = new URLSearchParams();
    params.set('collar_uuids', collarUuids.join(','));
    params.set('table_definition_ids', ids.join(','));
    const url = `${this.functionAppUrl}/api/drillz_geometry_visuals?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      throw new Error(result.message || result.error || `Geometry visuals HTTP ${response.status}`);
    }
    return result;
  }

  /**
   * @param {{ holeId?: string, collarUuid?: string, collarUuids?: string[] }} scope
   */
  async refreshGeometry(scope) {
    const headers = await this._headers();
    const body = {};
    if (scope.holeId) body.hole_id = scope.holeId;
    if (scope.collarUuids?.length) body.collar_uuids = scope.collarUuids;
    else if (scope.collarUuid) body.collar_uuids = [scope.collarUuid];

    const url = `${this.functionAppUrl}/api/drillz_geometry_refresh`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok || result.success === false) {
      throw new Error(result.message || result.error || `Geometry refresh HTTP ${response.status}`);
    }
    return result;
  }

  /**
   * Poll scene until pendingRefresh is false or maxAttempts reached.
   * @param {() => Promise<object>} fetchScene
   * @param {{ maxAttempts?: number, onPending?: (scene: object) => void }} [opts]
   */
  async pollUntilFresh(fetchScene, opts = {}) {
    const maxAttempts = opts.maxAttempts ?? 20;
    let scene = await fetchScene();
    let attempts = 0;
    while (scene?.freshness?.pendingRefresh && attempts < maxAttempts) {
      opts.onPending?.(scene);
      await new Promise((r) => setTimeout(r, POLL_MS));
      scene = await fetchScene();
      attempts += 1;
    }
    return scene;
  }

  /**
   * Poll in background with exponential backoff; calls onUpdate when scene refreshes.
   * Does not block the caller.
   * @param {() => Promise<object>} fetchScene
   * @param {{ onUpdate?: (scene: object) => void, onDone?: (scene: object) => void, signal?: AbortSignal }} [opts]
   */
  pollUntilFreshInBackground(fetchScene, opts = {}) {
    const maxAttempts = opts.maxAttempts ?? 10;
    let attempts = 0;
    let delayMs = POLL_MS;

    const tick = async () => {
      if (opts.signal?.aborted) return;
      try {
        const scene = await fetchScene();
        if (opts.signal?.aborted) return;
        if (!scene?.freshness?.pendingRefresh) {
          opts.onDone?.(scene);
          return;
        }
        opts.onUpdate?.(scene);
        attempts += 1;
        if (attempts >= maxAttempts) {
          opts.onDone?.(scene);
          return;
        }
        delayMs = Math.min(delayMs * 1.5, POLL_MAX_MS);
        setTimeout(tick, delayMs);
      } catch (e) {
        console.warn('[3D] background geometry poll failed:', e);
      }
    };

    setTimeout(tick, delayMs);
  }
}

export const geometryService = new GeometryService();
